/**
 * SPRINT-035: Hosted Checkout State Machine
 * PR-CHECKOUT-UI-CANONICAL-STATES-V1: Rendering-oriented reducer only.
 *
 * Pure reducer — no side effects, no I/O.
 * All transitions are deterministic given (state, event).
 *
 * Canonical backend→UI mapping is delegated entirely to runtime-state-adapter.ts.
 * This file contains NO direct runtime_state string comparisons.
 */

import type { CheckoutSession, AttemptResult, CheckoutError, PaymentOption, FieldDef, SavedMethodOneTap } from './types';
import { categorizeState } from './runtime-state-adapter';

export interface CardTokenResult {
  /** Wompi token id — safe to store, not a card number */
  payment_method_id: string;
  last4?: string;
  brand?: string;
  exp_month?: string;
  exp_year?: string;
}
import { getRedirectUrl } from './types';

export const POLL_TIMEOUT_MS = 60_000;
export const POLL_INTERVAL_MS = 2_000;
/** PR-CHECKOUT-LIVE-UPDATES-V1: degraded interval when SSE is connected (backend pushes updates). */
export const POLL_INTERVAL_MS_DEGRADED = 30_000;

// ============================================================================
// STATES
// ============================================================================

export type CheckoutState =
  | { status: 'INIT' }
  | { status: 'LOADING_SESSION'; pollStartedAt?: number }
  | { status: 'LOAD_ERROR'; error: CheckoutError }
  | { status: 'METHOD_SELECTION'; session: CheckoutSession; paymentOptions: PaymentOption[]; savedMethodOneTap?: SavedMethodOneTap | null }
  | { status: 'WALLET_CONFIRM'; session: CheckoutSession; paymentOptions: PaymentOption[] }
  | { status: 'COLLECTING_FIELDS'; session: CheckoutSession; paymentOptions: PaymentOption[]; selectedMethod: string; requiredFields: FieldDef[] }
  | {
      status: 'TOKENIZE_CARD';
      session: CheckoutSession;
      paymentOptions: PaymentOption[];
      savedMethodOneTap?: SavedMethodOneTap | null;
      /** Wompi public key — used to initialize the browser-side tokenization flow */
      publicKey: string;
      environment: 'sandbox' | 'production';
      /** [INVARIANT 17] User-safe error message after failed tokenization attempt */
      tokenizationError?: string;
    }
  | { status: 'PROCESSING'; session: CheckoutSession; selectedMethod: string }
  | { status: 'POLLING'; session: CheckoutSession; attempt: AttemptResult; pollStartedAt: number }
  | { status: 'QR_DISPLAY'; session: CheckoutSession; attempt: AttemptResult; pollStartedAt: number }
  | { status: 'REFERENCE_DISPLAY'; session: CheckoutSession; attempt: AttemptResult; pollStartedAt: number }
  | { status: 'REDIRECTING'; session: CheckoutSession; redirectUrl: string }
  /**
   * AWAITING_CONFIRMATION — payment intent submitted; system awaits external confirmation.
   * ADR-098: distinct from awaiting_customer_action (no further customer action needed here).
   * Polling continues — backend will eventually transition to completed or failed.
   */
  | {
      /**
       * STRIPE_CONFIRM — Stripe PaymentIntent created server-side; awaiting client-side
       * confirmation via Express Checkout Element (Apple Pay / Google Pay) or Payment Element (card).
       * Entered from PROCESSING when next_action.type === 'stripe_confirm'.
       * On STRIPE_CONFIRMED → AWAITING_CONFIRMATION (backend processes webhook).
       * ADR-098: maps to awaiting_customer_action until ECE/PE confirm fires.
       */
      status: 'STRIPE_CONFIRM';
      session: CheckoutSession;
      attempt: AttemptResult;
      clientSecret: string;
      publishableKey: string;
      pollStartedAt: number;
      stripeError?: string;
    }
  /**
   * PAYZEN_FORM — PayZen Krypton SmartForm rendered; customer completing card details.
   * Entered from PROCESSING when next_action.type === 'provider_form' and providerKey === 'payzen'.
   * PAYZEN_FORM_COMPLETED → AWAITING_CONFIRMATION (FINALITY GUARD: form submit ≠ payment confirmed).
   * Webhook/polling is the sole source of finality (ADR-098).
   */
  | {
      status: 'PAYZEN_FORM';
      session: CheckoutSession;
      attempt: AttemptResult;
      formToken: string;
      formPublicKey: string;
      pollStartedAt: number;
      payzenError?: string;
    }
  | { status: 'AWAITING_CONFIRMATION'; session: CheckoutSession; pollStartedAt: number }
  /**
   * STILL_PROCESSING — browser poll timeout elapsed but session is NOT failed.
   * Canonical runtime state: 'processing' or 'awaiting_customer_action'.
   * ADR-098: browser poll timeout is NOT a failure. Session lifecycle continues on backend.
   * The customer may safely close the tab and return later — canonical state persists.
   */
  | { status: 'STILL_PROCESSING'; session: CheckoutSession }
  | { status: 'SUCCESS_VERIFYING'; session: CheckoutSession }
  | { status: 'SUCCESS'; session: CheckoutSession }
  | { status: 'FAILURE'; session: CheckoutSession; error: CheckoutError; canRetry: boolean }
  | { status: 'EXPIRED'; session: CheckoutSession | null }
  | { status: 'CANCELLED'; session: CheckoutSession };

// ============================================================================
// EVENTS
// ============================================================================

export type CheckoutEvent =
  | { type: 'LOAD_SESSION' }
  | { type: 'SESSION_LOADED'; session: CheckoutSession; paymentOptions: PaymentOption[]; savedMethodOneTap?: SavedMethodOneTap | null }
  | { type: 'SESSION_LOAD_ERROR'; error: CheckoutError }
  | { type: 'SESSION_VERIFIED_COMPLETE'; session: CheckoutSession }
  | { type: 'SESSION_VERIFIED_NOT_COMPLETE' }
  | { type: 'SELECT_METHOD'; productKey: string }
  /** User selected a stored payment method — bypasses TOKENIZE_CARD entirely. */
  | { type: 'SELECT_SAVED_METHOD'; productKey: string; paymentMethodId: string }
  | { type: 'CONFIRM_WALLET' }
  | { type: 'CANCEL_WALLET' }
  | { type: 'FIELDS_SUBMITTED' }
  | { type: 'BACK_TO_METHODS' }
  /** [PR-14.25] Browser tokenization succeeded — token result carries payment_method_id only */
  | { type: 'CARD_TOKENIZED'; token: CardTokenResult }
  /** [PR-14.25] Browser tokenization failed — show user-safe error, allow retry */
  | { type: 'CARD_TOKENIZATION_FAILED'; error: string }
  | { type: 'ATTEMPT_CREATED'; attempt: AttemptResult }
  | { type: 'ATTEMPT_FAILED'; error: CheckoutError }
  /** STRIPE_CONFIRMED — stripe.confirmPayment() succeeded; backend webhook will complete the session. */
  | { type: 'STRIPE_CONFIRMED' }
  /** STRIPE_CONFIRM_FAILED — stripe.confirmPayment() returned an error; show inline, allow retry. */
  | { type: 'STRIPE_CONFIRM_FAILED'; error: string }
  /** PAYZEN_FORM_COMPLETED — Krypton SDK onSubmit fired; transitions to AWAITING_CONFIRMATION (FINALITY GUARD). */
  | { type: 'PAYZEN_FORM_COMPLETED' }
  /** PAYZEN_FORM_FAILED — Krypton SDK onError fired; show inline error, allow retry. */
  | { type: 'PAYZEN_FORM_FAILED'; error: string }
  | { type: 'ATTEMPT_IN_PROGRESS' }
  | { type: 'POLL_COMPLETED' }
  | { type: 'POLL_FAILED'; error: CheckoutError }
  /**
   * POLL_TIMEOUT — browser-local polling window elapsed.
   * ADR-098: this is NOT a failure. Transitions to STILL_PROCESSING, not FAILURE.
   * The canonical session state is authoritative; the browser's view is advisory.
   * CRS-005: No polling timeout maps directly to failed state.
   */
  | { type: 'POLL_TIMEOUT' }
  | { type: 'RETRY' }
  | { type: 'CANCEL_SESSION' };

// ============================================================================
// REDUCER
// ============================================================================

/** Route POLL_FAILED to the appropriate terminal state based on error code. */
function routePollFailed(
  session: CheckoutSession,
  error: CheckoutError,
): CheckoutState {
  if (error.code === 'SESSION_EXPIRED')   return { status: 'EXPIRED',   session };
  if (error.code === 'SESSION_CANCELLED') return { status: 'CANCELLED', session };
  return { status: 'FAILURE', session, error, canRetry: error.recoverable };
}

export function checkoutReducer(
  state: CheckoutState,
  event: CheckoutEvent,
): CheckoutState {
  switch (state.status) {

    case 'INIT':
      if (event.type === 'LOAD_SESSION') return { status: 'LOADING_SESSION' };
      break;

    case 'LOADING_SESSION':
      if (event.type === 'SESSION_LOADED') {
        const { session, paymentOptions } = event;

        // CRS-004 / ADR-098: canonical state interpretation delegated to adapter.
        // runtime_state is authoritative; session.status is the legacy fallback.
        const stateCategory = categorizeState(session.runtime_state, session.status);

        if (stateCategory === 'terminal_success')   return { status: 'SUCCESS',    session };
        if (stateCategory === 'terminal_expired')   return { status: 'EXPIRED',    session };
        if (stateCategory === 'terminal_cancelled') return { status: 'CANCELLED',  session };
        if (stateCategory === 'terminal_failed') {
          return {
            status: 'FAILURE',
            session,
            error: { code: 'PAYMENT_FAILED', message: 'Payment was not completed.', recoverable: false },
            canRetry: false,
          };
        }

        // awaiting_confirmation: intent submitted, system polling for external confirmation.
        // No attempt display needed — distinct from awaiting_customer_action.
        if (stateCategory === 'awaiting_confirmation' && session.current_attempt_id) {
          return { status: 'AWAITING_CONFIRMATION', session, pollStartedAt: Date.now() };
        }

        // In-progress attempt recovery: route to appropriate display by next_action type.
        const isInProgress = stateCategory === 'in_progress'
          || stateCategory === 'awaiting_customer_action';

        if (isInProgress && session.current_attempt_id) {
          const recoveredAttempt: AttemptResult = {
            attempt_id: session.current_attempt_id ?? '',
            attempt_no: 1,
            session_id: session.id,
            session_status: 'payment_initiated',
            payment_id: null,
            payment_status: 'pending',
            next_action: session.current_attempt?.next_action ?? { type: 'wait' },
          };
          const nextActionType = recoveredAttempt.next_action.type;

          // Preserve original pollStartedAt if carried through from a recovery trigger
          // so the countdown does not reset on tab-focus / visibilitychange.
          const existingPollStartedAt = state.pollStartedAt ?? Date.now();
          if (nextActionType === 'qr_code' || nextActionType === 'qr') {
            return { status: 'QR_DISPLAY', session, attempt: recoveredAttempt, pollStartedAt: existingPollStartedAt };
          }
          if (nextActionType === 'manual_reference' || nextActionType === 'reference') {
            return { status: 'REFERENCE_DISPLAY', session, attempt: recoveredAttempt, pollStartedAt: existingPollStartedAt };
          }
          return { status: 'POLLING', session, attempt: recoveredAttempt, pollStartedAt: existingPollStartedAt };
        }

        return { status: 'METHOD_SELECTION', session, paymentOptions, savedMethodOneTap: event.savedMethodOneTap ?? null };
      }
      if (event.type === 'SESSION_LOAD_ERROR') {
        // 410 / SESSION_EXPIRED vs actual load error
        if (
          event.error.code === 'SESSION_EXPIRED' ||
          event.error.code === 'GONE'
        ) {
          return { status: 'EXPIRED', session: null };
        }
        return { status: 'LOAD_ERROR', error: event.error };
      }
      break;

    case 'LOAD_ERROR':
      // Terminal — user must start fresh
      break;

    case 'METHOD_SELECTION':
      if (event.type === 'SELECT_METHOD') {
        const { productKey } = event;
        if (productKey === 'platform_wallet') {
          return {
            status: 'WALLET_CONFIRM',
            session: state.session,
            paymentOptions: state.paymentOptions,
          };
        }
        const option = state.paymentOptions.find((o) => o.methodKey === productKey);

        // [PR-14.25] Card with tokenization config → enter tokenization step before attempt
        if (option?.tokenizationConfig) {
          return {
            status: 'TOKENIZE_CARD',
            session: state.session,
            paymentOptions: state.paymentOptions,
            savedMethodOneTap: state.savedMethodOneTap,
            publicKey: option.tokenizationConfig.publicKey,
            environment: option.tokenizationConfig.environment,
          };
        }

        // If method requires field collection, show the form before creating attempt
        if (option?.requiredFields && option.requiredFields.length > 0) {
          return {
            status: 'COLLECTING_FIELDS',
            session: state.session,
            paymentOptions: state.paymentOptions,
            selectedMethod: productKey,
            requiredFields: option.requiredFields,
          };
        }
        return { status: 'PROCESSING', session: state.session, selectedMethod: productKey };
      }
      // Saved method selected — skip tokenization, go straight to PROCESSING
      if (event.type === 'SELECT_SAVED_METHOD') {
        return { status: 'PROCESSING', session: state.session, selectedMethod: event.productKey };
      }
      if (event.type === 'CANCEL_SESSION') {
        return { status: 'CANCELLED', session: state.session };
      }
      break;

    case 'TOKENIZE_CARD':
      // [PR-14.25] CARD_TOKENIZED → hand off to useCheckout which calls createAttempt then dispatches PROCESSING
      // The reducer itself just holds the tokenization state; the hook handles the async side effect.
      if (event.type === 'CARD_TOKENIZATION_FAILED') {
        // Stay in TOKENIZE_CARD — surface user-safe error message, user can retry
        return { ...state, tokenizationError: event.error };
      }
      if (event.type === 'CARD_TOKENIZED') {
        // Transition to PROCESSING; useCheckout will call createAttempt with the token
        return { status: 'PROCESSING', session: state.session, selectedMethod: 'card' };
      }
      if (event.type === 'BACK_TO_METHODS') {
        return { status: 'METHOD_SELECTION', session: state.session, paymentOptions: state.paymentOptions, savedMethodOneTap: state.savedMethodOneTap };
      }
      if (event.type === 'CANCEL_SESSION') {
        return { status: 'CANCELLED', session: state.session };
      }
      break;

    case 'COLLECTING_FIELDS':
      if (event.type === 'FIELDS_SUBMITTED') {
        return { status: 'PROCESSING', session: state.session, selectedMethod: state.selectedMethod };
      }
      if (event.type === 'BACK_TO_METHODS') {
        return { status: 'METHOD_SELECTION', session: state.session, paymentOptions: state.paymentOptions };
      }
      if (event.type === 'CANCEL_SESSION') {
        return { status: 'CANCELLED', session: state.session };
      }
      break;

    case 'WALLET_CONFIRM':
      if (event.type === 'CONFIRM_WALLET') {
        return { status: 'PROCESSING', session: state.session, selectedMethod: 'platform_wallet' };
      }
      if (event.type === 'CANCEL_WALLET') {
        return {
          status: 'METHOD_SELECTION',
          session: state.session,
          paymentOptions: state.paymentOptions,
        };
      }
      break;

    case 'PROCESSING':
      if (event.type === 'ATTEMPT_CREATED') {
        const { attempt } = event;

        // Payment already completed (e.g. synchronous wallet)
        if (attempt.payment_status === 'completed') {
          return { status: 'SUCCESS_VERIFYING', session: state.session };
        }

        // Provider requires redirect
        if (attempt.next_action.type === 'redirect') {
          const url = getRedirectUrl(attempt.next_action);
          if (url) {
            return { status: 'REDIRECTING', session: state.session, redirectUrl: url };
          }
        }

        // Stripe client-side confirmation — ECE (Apple Pay / Google Pay) + card fallback
        if (attempt.next_action.type === 'stripe_confirm') {
          const na = attempt.next_action as { clientSecret?: string; publishableKey?: string };
          if (na.clientSecret && na.publishableKey) {
            return {
              status: 'STRIPE_CONFIRM',
              session: state.session,
              attempt,
              clientSecret: na.clientSecret,
              publishableKey: na.publishableKey,
              pollStartedAt: Date.now(),
            };
          }
        }

        // PayZen embedded SmartForm — route by providerKey so future provider_form providers can coexist
        if (attempt.next_action.type === 'provider_form') {
          const na = attempt.next_action as { providerKey?: string; formToken?: string; formPublicKey?: string };
          if (na.providerKey === 'payzen' && na.formToken) {
            return {
              status: 'PAYZEN_FORM',
              session: state.session,
              attempt,
              formToken: na.formToken,
              formPublicKey: na.formPublicKey ?? '',
              pollStartedAt: Date.now(),
            };
          }
        }

        // BRE-B QR code: show QR image while polling
        if (attempt.next_action.type === 'qr_code' || attempt.next_action.type === 'qr') {
          return {
            status: 'QR_DISPLAY',
            session: state.session,
            attempt,
            pollStartedAt: Date.now(),
          };
        }

        // BRE-B Llave Dinámica: show reference key while polling
        if (attempt.next_action.type === 'manual_reference' || attempt.next_action.type === 'reference') {
          return {
            status: 'REFERENCE_DISPLAY',
            session: state.session,
            attempt,
            pollStartedAt: Date.now(),
          };
        }

        // All other cases: poll
        return {
          status: 'POLLING',
          session: state.session,
          attempt,
          pollStartedAt: Date.now(),
        };
      }
      if (event.type === 'ATTEMPT_FAILED') {
        return {
          status: 'FAILURE',
          session: state.session,
          error: event.error,
          canRetry: event.error.recoverable,
        };
      }
      if (event.type === 'ATTEMPT_IN_PROGRESS') {
        // Re-load session to recover current attempt state
        return { status: 'LOADING_SESSION' };
      }
      break;

    case 'PAYZEN_FORM':
      // FINALITY GUARD: Krypton onSubmit fired — form submitted to PayZen.
      // This is NOT payment confirmation. Transition to AWAITING_CONFIRMATION.
      // Webhook/polling is the sole source of finality (ADR-098).
      if (event.type === 'PAYZEN_FORM_COMPLETED') {
        return { status: 'AWAITING_CONFIRMATION', session: state.session, pollStartedAt: Date.now() };
      }
      // Inline error from Krypton onError — stay in PAYZEN_FORM; component re-renders error
      if (event.type === 'PAYZEN_FORM_FAILED') {
        return { ...state, payzenError: event.error };
      }
      // SSE may push a terminal state while customer is filling the form
      if (event.type === 'POLL_COMPLETED') {
        return { status: 'SUCCESS_VERIFYING', session: state.session };
      }
      if (event.type === 'POLL_FAILED') {
        return routePollFailed(state.session, event.error);
      }
      // PR-CHECKOUT-ASYNC-RECOVERY-UX-V1: re-fetch on visibility recovery
      if (event.type === 'LOAD_SESSION') {
        return { status: 'LOADING_SESSION', pollStartedAt: state.pollStartedAt };
      }
      if (event.type === 'CANCEL_SESSION') {
        return { status: 'CANCELLED', session: state.session };
      }
      break;

    case 'STRIPE_CONFIRM':
      // Customer confirmed via ECE (wallet) or Payment Element (card)
      if (event.type === 'STRIPE_CONFIRMED') {
        return { status: 'AWAITING_CONFIRMATION', session: state.session, pollStartedAt: Date.now() };
      }
      // Inline error — stay in STRIPE_CONFIRM, component renders error + card fallback
      if (event.type === 'STRIPE_CONFIRM_FAILED') {
        return { ...state, stripeError: event.error };
      }
      // SSE may push a terminal state while customer is on the ECE screen (e.g. session expired)
      if (event.type === 'POLL_COMPLETED') {
        return { status: 'SUCCESS_VERIFYING', session: state.session };
      }
      if (event.type === 'POLL_FAILED') {
        return routePollFailed(state.session, event.error);
      }
      // PR-CHECKOUT-ASYNC-RECOVERY-UX-V1: re-fetch on visibility recovery
      if (event.type === 'LOAD_SESSION') {
        return { status: 'LOADING_SESSION', pollStartedAt: state.pollStartedAt };
      }
      if (event.type === 'CANCEL_SESSION') {
        return { status: 'CANCELLED', session: state.session };
      }
      break;

    case 'POLLING': {
      if (event.type === 'POLL_COMPLETED') {
        return { status: 'SUCCESS_VERIFYING', session: state.session };
      }
      if (event.type === 'POLL_FAILED') {
        return routePollFailed(state.session, event.error);
      }
      // ADR-098: browser poll timeout is NOT a failure.
      // CRS-005: polling timeout must never map to failed state.
      // Backend session continues processing; customer may reopen later.
      if (event.type === 'POLL_TIMEOUT') {
        return { status: 'STILL_PROCESSING', session: state.session };
      }
      // PR-CHECKOUT-ASYNC-RECOVERY-UX-V1: re-fetch authoritative state on recovery trigger.
      if (event.type === 'LOAD_SESSION') {
        return { status: 'LOADING_SESSION', pollStartedAt: state.pollStartedAt };
      }
      break;
    }

    case 'QR_DISPLAY': {
      if (event.type === 'POLL_COMPLETED') {
        return { status: 'SUCCESS_VERIFYING', session: state.session };
      }
      if (event.type === 'POLL_FAILED') {
        return routePollFailed(state.session, event.error);
      }
      // ADR-098: browser poll timeout is NOT a failure.
      // QR may still be valid — backend session continues awaiting_customer_action.
      if (event.type === 'POLL_TIMEOUT') {
        return { status: 'STILL_PROCESSING', session: state.session };
      }
      // PR-CHECKOUT-ASYNC-RECOVERY-UX-V1: re-fetch authoritative state on recovery trigger.
      if (event.type === 'LOAD_SESSION') {
        return { status: 'LOADING_SESSION', pollStartedAt: state.pollStartedAt };
      }
      break;
    }

    case 'REFERENCE_DISPLAY': {
      if (event.type === 'POLL_COMPLETED') {
        return { status: 'SUCCESS_VERIFYING', session: state.session };
      }
      if (event.type === 'POLL_FAILED') {
        return routePollFailed(state.session, event.error);
      }
      // ADR-098: browser poll timeout is NOT a failure.
      // Reference transfer may still complete — customer may have initiated transfer.
      if (event.type === 'POLL_TIMEOUT') {
        return { status: 'STILL_PROCESSING', session: state.session };
      }
      // PR-CHECKOUT-ASYNC-RECOVERY-UX-V1: re-fetch authoritative state on recovery trigger.
      if (event.type === 'LOAD_SESSION') {
        return { status: 'LOADING_SESSION', pollStartedAt: state.pollStartedAt };
      }
      break;
    }

    case 'AWAITING_CONFIRMATION': {
      // Intent submitted; awaiting external confirmation. Continue polling.
      if (event.type === 'POLL_COMPLETED') {
        return { status: 'SUCCESS_VERIFYING', session: state.session };
      }
      if (event.type === 'POLL_FAILED') {
        return routePollFailed(state.session, event.error);
      }
      // ADR-098: poll timeout is not failure — backend may still confirm.
      if (event.type === 'POLL_TIMEOUT') {
        return { status: 'STILL_PROCESSING', session: state.session };
      }
      if (event.type === 'LOAD_SESSION') {
        return { status: 'LOADING_SESSION', pollStartedAt: state.pollStartedAt };
      }
      break;
    }

    case 'SUCCESS_VERIFYING':
      // INVARIANT: Re-fetch session before marking SUCCESS — use fresh session
      if (event.type === 'SESSION_VERIFIED_COMPLETE') {
        return { status: 'SUCCESS', session: event.session };
      }
      if (event.type === 'SESSION_VERIFIED_NOT_COMPLETE') {
        // Payment may still be processing — stay on verifying (component retries)
        return state;
      }
      break;

    case 'STILL_PROCESSING':
      // Backend session is still processing — no customer action needed, no failure.
      // On page refresh or explicit "check again" action, reload the session.
      if (event.type === 'LOAD_SESSION') {
        return { status: 'LOADING_SESSION' };
      }
      // POLL_COMPLETED means the backend reconciled while we were in STILL_PROCESSING
      if (event.type === 'POLL_COMPLETED') {
        return { status: 'SUCCESS_VERIFYING', session: state.session };
      }
      break;

    case 'FAILURE':
      if (event.type === 'RETRY' && state.canRetry) {
        return { status: 'LOADING_SESSION' };
      }
      break;

    case 'REDIRECTING':
    case 'SUCCESS':
    case 'EXPIRED':
    case 'CANCELLED':
      // Terminal states — no transitions
      break;
  }

  return state;
}
